import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Card, Row, Col, Form, Button } from 'react-bootstrap';
import moment from 'moment-timezone';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Markdown from 'react-markdown';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faEnvelope,
  faPhone,
  faPen,
  faPlus,
  faCopy,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import RcCallModal from '../../ringcentral/RcCallModal';

import { useUser } from '../../../contexts/UserContext';
import { RoundButton, Label, Value } from './Profile.elements';
import { TIMEZONES } from '../../common/TimezoneSelect';
import EditClaimModal from '../../claim/EditClaimModal';
import UserPickerModal from './UserPickerModal';
import SubmitToVaModal from '../../claim/SubmitToVaModal';
import {
  useToggleClaimTask,
  useConfirmClaimRating,
  useRequestClaimDiscount,
  useReviewClaimDiscount,
  useUpdateClaim,
  useSetClaimAssignment,
} from '../../../services/claims';
import { ucwords } from '../../../lib/strings';
import { formatPhone } from '../../operations/leads/leadHelpers';
import { noIndentedCode } from '../../../lib/markdownPlugins';
import day from '../../../lib/day';
import { availableTransitions } from '../../../lib/claimHelpers';
import ConfirmRatingModal from './ConfirmRatingModal';
import DiscountRequestModal from './DiscountRequestModal';
import ClaimHandoffCard from './ClaimHandoffCard';

// Safety net for pasted note content the parser tweak doesn't cover (very
// long URLs, non-breaking-space paragraphs, fenced code blocks): let anything
// wrap rather than force the sidebar column wider than the viewport.
const NotesStyled = styled.div`
  overflow-wrap: anywhere;

  pre,
  code {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

// Overline label shared by the CM card and the claim-details card below it,
// so the two read as one premium unit.
const SectionLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
`;

// One-click copy with a quiet inline confirmation (icon flips to a green
// check for a moment) instead of a toast.
const CopyIconButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="link"
      size="sm"
      className="p-0"
      title={`Copy ${label}`}
      style={{ lineHeight: 1, border: 'none' }}
      onClick={() => {
        if (!navigator.clipboard) return;
        navigator.clipboard
          .writeText(text)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => {});
      }}
    >
      <FontAwesomeIcon
        icon={copied ? faCheck : faCopy}
        style={{
          fontSize: '0.72rem',
          color: copied ? '#16a34a' : '#c2cad6',
        }}
      />
    </Button>
  );
};

const CmAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.02em;
  color: #fff;
  background: ${(p) =>
    p.$muted
      ? 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)'
      : 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)'};
  box-shadow: ${(p) =>
    p.$muted ? 'none' : '0 2px 6px rgba(67, 56, 202, 0.35)'};
`;

/**
 * The assigned claims manager, front and center for everyone on the claim
 * (providers included). Shown from In Process onward; before that the plain
 * "Assigned To" picker row renders in the assignment grid instead.
 * ROLE-BASED truth (Nic, 2026-08-03): only actual claims_manager /
 * claims_admin users get the "Claims Manager" label. Anyone else in the
 * seat (onboarding specialist standing in, a director, an admin) shows
 * their REAL role and an amber flag that a Claims Manager still needs to
 * be assigned — calling Jorge or Chris Rose "Claims Manager" was actively
 * misleading.
 */
const CM_USER_TYPES = ['claims_manager', 'claims_admin'];
const ROLE_LABELS = {
  onboarding_specialist: 'Onboarding Specialist',
  onboarding_manager: 'Onboarding Manager',
  director: 'Director',
  admin: 'Admin',
  referral: 'Referral Partner',
  it_support: 'IT Support',
};
const roleLabelFor = (userType) =>
  ROLE_LABELS[userType] ||
  String(userType || '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ') ||
  'Assigned To';

const AssignedCmCard = ({ assignedUser, canEdit, onEdit }) => {
  const isRealCm =
    !!assignedUser && CM_USER_TYPES.includes(assignedUser.userType);
  // Nothing useful to show a read-only viewer when no CM is assigned.
  if (!assignedUser && !canEdit) return null;

  const name = assignedUser
    ? `${assignedUser.userFirstName || ''} ${
        assignedUser.userLastName || ''
      }`.trim()
    : '';
  const cmInitials = assignedUser
    ? `${(assignedUser.userFirstName || '').trim().charAt(0)}${(
        assignedUser.userLastName || ''
      )
        .trim()
        .charAt(0)}`.toUpperCase() || '?'
    : '?';
  const phone = assignedUser?.internalUserPhone || null;
  const contactRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem',
    fontSize: '0.85rem',
    marginTop: '3px',
    minWidth: 0,
  };
  const contactIconStyle = {
    color: '#94a3b8',
    fontSize: '0.75rem',
    width: '0.9rem',
  };
  const contactLinkStyle = { color: '#334155', textDecoration: 'none' };

  return (
    <Card className="mb-3">
      <Card.Body
        className="d-flex align-items-center"
        style={{ gap: '0.9rem' }}
      >
        <CmAvatar $muted={!assignedUser}>{cmInitials}</CmAvatar>
        <div style={{ minWidth: 0, flex: 1 }}>
          <SectionLabel style={{ marginBottom: 0 }}>
            {!assignedUser || isRealCm
              ? 'Claims Manager'
              : roleLabelFor(assignedUser.userType)}
          </SectionLabel>
          {assignedUser ? (
            <>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: '#1f2937',
                  lineHeight: 1.25,
                }}
              >
                {name}
              </div>
              {phone ? (
                // Display only, deliberately not a tel: link (Nic's call).
                <div style={contactRowStyle}>
                  <FontAwesomeIcon icon={faPhone} style={contactIconStyle} />
                  <span style={contactLinkStyle}>{formatPhone(phone)}</span>
                  <CopyIconButton text={formatPhone(phone)} label="phone number" />
                </div>
              ) : null}
              <div style={contactRowStyle}>
                <FontAwesomeIcon icon={faEnvelope} style={contactIconStyle} />
                <a
                  href={`mailto:${assignedUser.userEmail}`}
                  style={{
                    ...contactLinkStyle,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {assignedUser.userEmail}
                </a>
                <CopyIconButton text={assignedUser.userEmail} label="email" />
              </div>
              {!isRealCm ? (
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '0.8rem',
                    color: '#b45309',
                    fontWeight: 600,
                  }}
                >
                  No Claims Manager assigned yet.
                  {canEdit ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-1 align-baseline"
                      style={{ fontSize: '0.8rem' }}
                      onClick={onEdit}
                    >
                      Assign one
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <Button
              variant="link"
              className="p-0 text-start"
              onClick={onEdit}
              style={{ fontSize: '0.95rem' }}
            >
              Unassigned. Assign a claims manager
            </Button>
          )}
        </div>
        {canEdit && assignedUser ? (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-muted align-self-start"
            onClick={onEdit}
            title="Change the assigned claims manager"
          >
            <FontAwesomeIcon icon={faPen} />
          </Button>
        ) : null}
      </Card.Body>
    </Card>
  );
};

const formatDiscountPercent = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '--';
  return Number.isInteger(parsed)
    ? `${parsed}%`
    : `${parsed.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`;
};

const formatDiscountPresetOption = (preset) => (
  `${preset.label} (${formatDiscountPercent(preset.percent)})`
);

const formatDiscountRequest = (request) => {
  if (!request) return '--';
  const label = request.approvedPresetLabel || request.requestedPresetLabel;
  const percent = request.approvedPercent ?? request.requestedPercent;
  const formattedPercent = formatDiscountPercent(percent);

  if (label && formattedPercent !== '--') {
    return `${label} (${formattedPercent})`;
  }
  return label || formattedPercent;
};

const SideBar = ({ data, user, onCreateActivityClicked }) => {
  // View only (2026-10-09): staff who can see this claim but not change it
  // (Sales Reps; Onboarding Specialists once it's past onboarding). Every
  // edit control below locks or hides; the server refuses changes anyway.
  const viewOnly = data?.canEdit === false;
  const [showFullTaskList, setShowFullTaskList] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(null);
  const [edit, setEdit] = useState(false);
  const [showSubmitToVa, setShowSubmitToVa] = useState(false);
  const [showConfirmRating, setShowConfirmRating] = useState(false);
  const [showDiscountRequest, setShowDiscountRequest] = useState(false);
  const [discountReviewPresetKey, setDiscountReviewPresetKey] = useState('');
  const [discountReviewNote, setDiscountReviewNote] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [callTarget, setCallTarget] = useState(null);
  const { hasPermission, hasRole, permissions } = useUser();
  // Referred By is an admin's correction only (Nic 2026-10-09).
  const canEditReferredBy = hasRole('admin');
  // Status and the assignments are set elsewhere for Onboarding Specialists
  // and Sales Reps (Nic 2026-10-09): status follows Client Onboarding, the
  // specialist is changed by Reassign there, the CM on CM Assignment. They
  // never set these by hand, even while the claim is in onboarding.
  const lockAssignments =
    viewOnly ||
    (!hasPermission('billing:manage') && !hasPermission('claims:edit_after_onboarding'));

  const isAdminRoleAdmin = hasPermission('claims:change_status');

  const claimStatusHistoryItems = useMemo(() => {
    if (!isAdminRoleAdmin) return [];
    const items = [];

    // Status-change event_log history for this claim (admin-only)
    const eventLog = Array.isArray(data?.eventLog) ? data.eventLog : [];
    eventLog.forEach((e) => {
      const ts = e && e.eventDate ? moment.utc(e.eventDate).valueOf() : null;
      if (!ts) return;
      const body = e && e.eventBody && typeof e.eventBody === 'object' ? e.eventBody : {};
      const prev = body.previousStatus ? String(body.previousStatus) : '';
      const next = body.newStatus ? String(body.newStatus) : '';
      if (!prev && !next) return;

      items.push({
        id: `status-${e.eventId || ts}`,
        ts,
        fromStatus: prev || '—',
        toStatus: next || '—',
      });
    });

    // newest first
    return items.sort((a, b) => b.ts - a.ts);
  }, [data, isAdminRoleAdmin]);

  const toggleClaimTask = useToggleClaimTask(
    () => {
      queryClient.invalidateQueries('claim-profile');
      toast.success('The task has been successfully toggled.');
    },
    { onError: (err) => toast.error(err.message) },
  );

  const setClaimAssignment = useSetClaimAssignment(
    (_, variables) => {
      setShowUserPicker(null);
      toast.success('Your changes have been successfully saved.');

      // If user only has view_assigned and reassigned the claim away, redirect to dashboard
      // (unless they're still the onboarding assignee and can still see the claim)
      const canViewAll = hasPermission('claims:view_all');
      const assignedAway =
        variables?.body?.claimAssignedToUserId &&
        variables.body.claimAssignedToUserId !== user.userId;
      const stillOnboardingAssignee =
        data.claim.onboardingAssignedToUserId === user.userId;

      if (!canViewAll && assignedAway && !stillOnboardingAssignee) {
        navigate('/');
      } else {
        queryClient.invalidateQueries('claim-profile');
      }
    },
    { onError: (err) => toast.error(err.message) },
  );

  const updateClaim = useUpdateClaim(
    () => {
      toast.success('Claim status was successfully updated.');
      queryClient.invalidateQueries('claim-profile');
    },
    { onError: (err) => toast.error(err.message) },
  );

  const confirmClaimRating = useConfirmClaimRating(
    () => {
      setShowConfirmRating(false);
      toast.success('Rating updated.');
      queryClient.invalidateQueries('claim-profile');
    },
    { onError: (err) => toast.error(err.message) },
  );

  const requestClaimDiscount = useRequestClaimDiscount(
    () => {
      setShowDiscountRequest(false);
      toast.success('Discount request sent.');
      queryClient.invalidateQueries('claim-profile');
      queryClient.invalidateQueries(['claim-profile', data.claim.claimId]);
    },
    {
      onError: (err) =>
        toast.error(err.response?.data?.msg || err.message),
    },
  );

  const reviewClaimDiscount = useReviewClaimDiscount(
    (_, variables) => {
      toast.success(
        variables?.body?.action === 'approve'
          ? 'Discount approved.'
          : 'Discount rejected.',
      );
      queryClient.invalidateQueries('claim-profile');
      queryClient.invalidateQueries(['claim-profile', data.claim.claimId]);
    },
    {
      onError: (err) =>
        toast.error(err.response?.data?.msg || err.message),
    },
  );

  const onValueUpdate = (field, value) => {
    // Intercept status change to "submitted to va" and show the modal
    if (field === 'claimStatus' && value === 'submitted to va') {
      setShowSubmitToVa(true);
      return;
    }

    updateClaim.mutate({
      claimId: data.claim.claimId,
      payload: { [field]: value },
    });
  };

  const [tasks, completedTasks, totalRemainingTasks] = useMemo(() => {
    const list = data.tasks.filter((t) => t.claimTaskCompletedOn === null);
    const completed = data.tasks.filter((t) => t.claimTaskCompletedOn !== null);
    if (!showFullTaskList) {
      return [list.slice(0, 3), completed, list.length];
    }
    return [list, completed, list.length];
  }, [data.tasks, showFullTaskList]);

  const currentClaimRating = useMemo(() => (
    data.user.userCurrentVaRating ?? null
  ), [
    data.user.userCurrentVaRating,
  ]);

  const ratingLastSetOn = useMemo(() => (
    data.user.userCurrentVaRatingOn ||
    data.latestRetellRatingVerification?.verifiedAt ||
    null
  ), [
    data.user.userCurrentVaRatingOn,
    data.latestRetellRatingVerification?.verifiedAt,
  ]);

  const ratingOpsPills = Array.isArray(data.ratingOps?.pills)
    ? data.ratingOps.pills
    : [];

  const canConfirmRating = useMemo(() => {
    if (viewOnly || !hasPermission('claims:update_rating')) return false;
    if (hasPermission('claims:view_all')) return true;
    const actorId = String(user.userId || '');
    return [
      data.claim.claimAssignedToUserId,
      data.claim.onboardingAssignedToUserId,
    ].some((assignedUserId) => (
      assignedUserId && String(assignedUserId) === actorId
    ));
  }, [
    data.claim.claimAssignedToUserId,
    data.claim.onboardingAssignedToUserId,
    hasPermission,
    user.userId,
    viewOnly,
  ]);

  const discountRequests = Array.isArray(data.discountRequests)
    ? data.discountRequests
    : [];
  const discountPresets = useMemo(() => (
    Array.isArray(data.discountPresets) ? data.discountPresets : []
  ), [data.discountPresets]);
  const latestDiscountRequest =
    data.latestDiscountRequest || discountRequests[0] || null;
  const latestDiscountRequestId =
    latestDiscountRequest?.claimDiscountRequestId || null;
  const latestDiscountRequestStatus = latestDiscountRequest?.status || null;
  const latestDiscountRequestedPercent =
    latestDiscountRequest?.requestedPercent ?? null;
  const latestDiscountRequestedPresetKey =
    latestDiscountRequest?.requestedPresetKey || null;
  const hasPendingDiscountRequest =
    latestDiscountRequestStatus === 'pending';
  const canRequestDiscount = useMemo(() => {
    if (!hasPermission('claims:edit')) return false;
    if (hasPermission('claims:view_all')) return true;
    return data.claim.claimAssignedToUserId === user.userId;
  }, [data.claim.claimAssignedToUserId, hasPermission, user.userId]);
  const isDiscountApprover =
    String(user?.userEmail || '').trim().toLowerCase() ===
    String(data.discountApproverEmail || 'corbitt@veritasratings.com')
      .trim()
      .toLowerCase();

  useEffect(() => {
    if (latestDiscountRequestStatus !== 'pending') {
      setDiscountReviewPresetKey('');
      setDiscountReviewNote('');
      return;
    }

    const matchingPresets = discountPresets.filter(
      (preset) => Number(preset.percent) === Number(latestDiscountRequestedPercent),
    );
    setDiscountReviewPresetKey(
      latestDiscountRequestedPresetKey ||
        (matchingPresets.length === 1 ? matchingPresets[0].key : ''),
    );
    setDiscountReviewNote('');
  }, [
    discountPresets,
    latestDiscountRequestId,
    latestDiscountRequestedPercent,
    latestDiscountRequestedPresetKey,
    latestDiscountRequestStatus,
  ]);

  const clientAddressVerified = useMemo(() => {
    const u = data?.user;
    if (!u) return false;
    const stringsOk =
      typeof u.addressLine1 === 'string' && u.addressLine1.trim().length > 0 &&
      typeof u.addressCity === 'string' && u.addressCity.trim().length > 0 &&
      typeof u.addressState === 'string' && u.addressState.trim().length > 0 &&
      typeof u.addressZip === 'string' && u.addressZip.trim().length > 0;
    const latLngOk = u.latitude != null && u.longitude != null;
    return stringsOk && latLngOk;
  }, [data?.user]);

  // Match the backend gate predicate: only block advance options when this
  // user is flagged AND the address isn't verified yet. Legacy users
  // (requireAddress = false) move freely.
  const addressGateActive =
    !!data?.user?.requireAddress && !clientAddressVerified;
  const SIDEBAR_ENTRY_STATUSES = ['intake', 'awaiting medical records'];
  const SIDEBAR_NEUTRAL_STATUSES = [...SIDEBAR_ENTRY_STATUSES, 'dead'];

  // Assigned-CM card takes over from the plain "Assigned To" row once the
  // claim leaves the entry stage (Dead included: it still shows who owned it).
  const showCmCard =
    !!data?.claim?.claimStatus &&
    !SIDEBAR_ENTRY_STATUSES.includes(data.claim.claimStatus);
  // Who the CM is: admins, claims admins and CM Assignment only, never the
  // CM themselves (Nic 2026-10-09). The server refuses it too.
  const canAssignCm =
    hasRole('admin') || hasRole('claims_admin') || hasPermission('cm_assignment:manage');
  // Before In Process the Claims Manager is CM Assignment's to set (Nic
  // 2026-10-30): nobody edits it here, admins included. The server refuses
  // it too.
  const cmLockedInOnboarding = SIDEBAR_NEUTRAL_STATUSES.includes(data?.claim?.claimStatus);
  const canEditAssignment = !lockAssignments && canAssignCm && !cmLockedInOnboarding;
  const openAssignedToPicker = () =>
    setShowUserPicker({
      label: 'Set Assigned To',
      userType: 'internal',
      value: data.claim.assignedUser
        ? {
            value: data.claim.assignedUser.userId,
            label: `${data.claim.assignedUser.userFirstName} ${data.claim.assignedUser.userLastName}`,
          }
        : null,
      onSelect: (val) =>
        setClaimAssignment.mutate({
          claimId: data.claim.claimId,
          body: {
            claimAssignedToUserId: val ? val.value : null,
          },
        }),
    });
  const transitionNeedsVerifiedAddress = (newStatus) => {
    const prev = data?.claim?.claimStatus;
    if (!newStatus || newStatus === prev) return false;
    const prevIsEntry = prev == null || SIDEBAR_ENTRY_STATUSES.includes(prev);
    const newIsAdvance = !SIDEBAR_NEUTRAL_STATUSES.includes(newStatus);
    return prevIsEntry && newIsAdvance;
  };

  const availStatuses = useMemo(() => {
    if (!data || !data.claim) {
      return [];
    }

    const newStatuses = availableTransitions(
      data.claim.claimStatus,
      permissions,
    );

    return [...newStatuses, data.claim.claimStatus];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, permissions]);

  const handleDiscountReview = (action) => {
    if (!latestDiscountRequest) return;
    reviewClaimDiscount.mutate({
      claimId: data.claim.claimId,
      requestId: latestDiscountRequest.claimDiscountRequestId,
      body: {
        action,
        presetKey:
          action === 'approve' ? discountReviewPresetKey : undefined,
        reviewNote: discountReviewNote,
      },
    });
  };
  const canApproveDiscount = Boolean(discountReviewPresetKey);
  const discountCard = (canRequestDiscount || latestDiscountRequest) ? (
    <Card className="mt-3 mb-3">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <Label>Discount</Label>
          {canRequestDiscount ? (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => setShowDiscountRequest(true)}
              disabled={hasPendingDiscountRequest}
            >
              Request Discount
            </Button>
          ) : null}
        </div>

        {!latestDiscountRequest ? (
          <div className="text-muted small">No discount requested.</div>
        ) : (
          <>
            <div className="d-flex align-items-center mb-2" style={{ gap: '0.5rem' }}>
              <Badge
                bg={
                  latestDiscountRequest.status === 'approved'
                    ? 'success'
                    : latestDiscountRequest.status === 'rejected'
                      ? 'secondary'
                      : 'warning'
                }
                text={latestDiscountRequest.status === 'pending' ? 'dark' : undefined}
              >
                {ucwords(latestDiscountRequest.status)}
              </Badge>
              <span className="fw-semibold">
                {formatDiscountRequest(latestDiscountRequest)}
              </span>
            </div>

            <div className="text-muted small">
              Requested by {latestDiscountRequest.requestedByName || 'Unknown'}
              {latestDiscountRequest.createdAt
                ? ` on ${moment.utc(latestDiscountRequest.createdAt).format('MM/DD/YYYY h:mm A')}`
                : ''}
            </div>

            {latestDiscountRequest.requestNote ? (
              <div className="small mt-2">{latestDiscountRequest.requestNote}</div>
            ) : null}

            {latestDiscountRequest.status === 'approved' ? (
              <Alert variant="success" className="small mt-3 mb-0">
                {latestDiscountRequest.appliedAt
                  ? `Active. Last applied to an eligible invoice on ${moment.utc(latestDiscountRequest.appliedAt).format('MM/DD/YYYY h:mm A')}.`
                  : 'Active and ready to apply to future rating invoices.'}
              </Alert>
            ) : null}

            {latestDiscountRequest.status === 'pending' && isDiscountApprover ? (
              <div className="mt-3 pt-3 border-top">
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Approved discount</Form.Label>
                  <Form.Select
                    size="sm"
                    value={discountReviewPresetKey}
                    onChange={(event) => setDiscountReviewPresetKey(event.target.value)}
                    disabled={reviewClaimDiscount.isLoading}
                  >
                    <option value="">Select a discount</option>
                    {discountPresets.map((preset) => (
                      <option key={preset.key} value={preset.key}>
                        {formatDiscountPresetOption(preset)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Review note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    value={discountReviewNote}
                    onChange={(event) => setDiscountReviewNote(event.target.value)}
                    disabled={reviewClaimDiscount.isLoading}
                  />
                </Form.Group>
                <div className="d-flex" style={{ gap: '0.5rem' }}>
                  <Button
                    size="sm"
                    variant="success"
                    disabled={reviewClaimDiscount.isLoading || !canApproveDiscount}
                    onClick={() => handleDiscountReview('approve')}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={reviewClaimDiscount.isLoading}
                    onClick={() => handleDiscountReview('reject')}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card.Body>
    </Card>
  ) : null;

  // Rendered next to Referred By while the CM card owns the Assigned To slot,
  // and in its classic spot on entry statuses - keeps the grid gap-free.
  const assignedProvidersCol = (
    <Col xs={6} key="assigned-providers">
      <SectionLabel>Assigned Providers</SectionLabel>
      <Value>
        <Button
          variant="link"
          className="p-0 text-start"
          disabled={lockAssignments}
          onClick={() =>
            setShowUserPicker({
              label: 'Set Provider To',
              userType: 'provider',
              isMulti: true,
              value: data.providers
                ? data.providers.map((p) => ({
                    value: p.userId,
                    label: `${p.userFirstName} ${p.userLastName}`,
                  }))
                : null,
              onSelect: (val) =>
                setClaimAssignment.mutate({
                  claimId: data.claim.claimId,
                  body: {
                    providers:
                      val && val.length > 0
                        ? val.map((p) => p.value)
                        : null,
                  },
                }),
            })
          }
        >
          {data.providers && data.providers.length > 0
            ? data.providers
                .map(
                  (provider) =>
                    `${provider.userFirstName} ${provider.userLastName}`,
                )
                .join(', ')
            : 'Unassigned'}
        </Button>
      </Value>
    </Col>
  );

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h2 className="text-center mb-1" style={{ color: '#445768' }}>
            {data.user.userFirstName} {data.user.userLastName}
          </h2>
          {data.user.userPreferredName ? (
            <p className="text-center text-muted mb-3" style={{ fontSize: '0.9rem' }}>
              &ldquo;{data.user.userPreferredName}&rdquo;
            </p>
          ) : <div className="mb-3" />}
          <div
            className="d-flex justify-content-center"
            style={{ gap: '2rem' }}
          >
            <RoundButton
              variant="link"
              size="sm"
              className="btn-round"
              onClick={() => navigate(-1)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </RoundButton>
            <RoundButton
              variant="link"
              size="sm"
              className="btn-round"
              onClick={() => window.open(`mailto:${data.user.userEmail}`)}
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </RoundButton>
            <RoundButton
              variant="link"
              size="sm"
              className="btn-round"
              onClick={() => {
                setCallTarget(data.user.userPhone);
              }}
              disabled={data.user.userPhone === null}
            >
              <FontAwesomeIcon icon={faPhone} />
            </RoundButton>
            {viewOnly ? null : (
              <>
                <RoundButton
                  variant="link"
                  size="sm"
                  className="btn-round"
                  onClick={() => setEdit(true)}
                >
                  <FontAwesomeIcon icon={faPen} fixedWidth />
                </RoundButton>
                <RoundButton
                  variant="link"
                  size="sm"
                  className="btn-round"
                  onClick={() => onCreateActivityClicked()}
                >
                  <FontAwesomeIcon icon={faPlus} fixedWidth />
                </RoundButton>
              </>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* View only (2026-10-09): this viewer can see the claim but not
          change it; the server refuses any change too. */}
      {data.canEdit === false ? (
        <Alert variant="secondary" className="small mb-3">
          <strong>View only.</strong>{' '}
          {['intake', 'awaiting medical records'].includes(data.claim.claimStatus)
            ? 'You can see this claim but not change it.'
            : 'This claim has moved past onboarding and is with its Claims Manager.'}
        </Alert>
      ) : null}

      {showCmCard ? (
        <AssignedCmCard
          assignedUser={data.claim.assignedUser}
          canEdit={canEditAssignment}
          onEdit={openAssignedToPicker}
        />
      ) : null}

      <ClaimHandoffCard claim={data.claim} canEdit={data.canEdit !== false} />

      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col xs={6}>
              <SectionLabel>Status</SectionLabel>
              <Form.Select
                name="claimStatus"
                value={data.claim.claimStatus}
                disabled={lockAssignments}
                size="sm"
                className="mb-3"
                onChange={(e) => onValueUpdate(e.target.name, e.target.value)}
              >
                {[
                  'intake',
                  'awaiting medical records',
                  'in process',
                  'awaiting internal review',
                  'internal review approved',
                  'submitted to va',
                  'invoiced',
                  'payment plan',
                  'paid',
                  'dispute hold',
                  'dead',
                ]
                  // Intake and Awaiting Med. Recs. follow Client Onboarding
                  // (Nic 2026-10-09): never offered here, even with the
                  // status override. Shown only as the claim's current
                  // status, and Intake on a Dead claim (back to onboarding).
                  .filter((opt) => {
                    if (!['intake', 'awaiting medical records'].includes(opt)) return true;
                    const current = data.claim.claimStatus;
                    return opt === current || (opt === 'intake' && current === 'dead');
                  })
                  .map((opt, optIdx) => {
                  const blockedByAddressGate =
                    addressGateActive && transitionNeedsVerifiedAddress(opt);
                  const revive = opt === 'intake' && data.claim.claimStatus === 'dead';
                  return (
                    <option
                      value={opt}
                      key={optIdx}
                      disabled={!availStatuses.includes(opt) || blockedByAddressGate}
                    >
                      {revive ? 'Intake (back to the Onboarding Workspace)' : ucwords(opt)}
                      {blockedByAddressGate ? ' (verify client address first)' : ''}
                    </option>
                  );
                })}
              </Form.Select>
              {addressGateActive ? (
                <small className="text-muted d-block mb-3" style={{ marginTop: '-0.5rem' }}>
                  Add a verified address for the client to advance past the
                  entry stage.
                </small>
              ) : null}
            </Col>
            <Col xs={6}>
              <SectionLabel>VA Claim Type</SectionLabel>
              <Form.Select
                name="claimVaType"
                value={data.claim.claimVaType}
                disabled={lockAssignments}
                size="sm"
                className="mb-3"
                onChange={(e) => onValueUpdate(e.target.name, e.target.value)}
              >
                {[
                  'initial',
                  'supplemental appeal',
                  'higher rate review',
                  'VA court appeal',
                ].map((opt, optIdx) => {
                  return (
                    <option value={opt} key={optIdx}>
                      {ucwords(opt)}
                    </option>
                  );
                })}
              </Form.Select>
            </Col>
          </Row>

          <hr
            style={{
              border: 'none',
              borderTop: '1px solid #eef2f7',
              margin: '0.25rem 0 1rem',
            }}
          />

          <Row>
            <Col xs={6}>
              <SectionLabel>Referred By</SectionLabel>
              <Value>
                {data.claim.referredByContact &&
                (data.claim.referredByContact.firstName ||
                  data.claim.referredByContact.lastName) ? (
                  // The individual website referrer (Referral Program leads),
                  // captured from the originating lead. Read-only; the channel
                  // itself shows under "Partner Referral" below.
                  <span>
                    {`${data.claim.referredByContact.firstName || ''} ${
                      data.claim.referredByContact.lastName || ''
                    }`.trim()}
                  </span>
                ) : data.claim.partnerName ? (
                  // Partner attribution is set-once at signup (via the lead/invite
                  // flow or a partner referral URL) and is not editable from the
                  // SideBar — prevents accidentally hiding partner credit by
                  // adding an internal-user referrer on top.
                  <span>{data.claim.partnerName}</span>
                ) : (
                  <Button
                    variant="link"
                    className="p-0"
                    disabled={!canEditReferredBy}
                    onClick={() =>
                      setShowUserPicker({
                        label: 'Set Referred By',
                        userType: 'internal',
                        value: data.claim.refUser
                          ? {
                              value: data.claim.refUser.userId,
                              label: `${data.claim.refUser.userFirstName} ${data.claim.refUser.userLastName}`,
                            }
                          : null,
                        onSelect: (val) =>
                          setClaimAssignment.mutate({
                            claimId: data.claim.claimId,
                            body: { userReferredBy: val ? val.value : null },
                          }),
                      })
                    }
                  >
                    {data.claim.refUser
                      ? `${data.claim.refUser.userFirstName} ${data.claim.refUser.userLastName}`
                      : 'Unassigned'}
                  </Button>
                )}
              </Value>
            </Col>
            {!showCmCard ? (
              // From In Process onward the AssignedCmCard above replaces this
              // cell; before that it only shows who it is: CM Assignment
              // sets the Claims Manager (Nic 2026-10-30).
              <Col xs={6}>
                <SectionLabel>Assigned To</SectionLabel>
                <Value>
                  <Button
                    variant="link"
                    className="p-0"
                    disabled={!canEditAssignment}
                    onClick={openAssignedToPicker}
                  >
                    {data.claim.assignedUser
                      ? `${data.claim.assignedUser.userFirstName} ${data.claim.assignedUser.userLastName}`
                      : 'Unassigned'}
                  </Button>
                  {cmLockedInOnboarding && canAssignCm && !viewOnly ? (
                    <div className="small text-muted">Set on CM Assignment</div>
                  ) : null}
                </Value>
              </Col>
            ) : (
              assignedProvidersCol
            )}
            <Col xs={6}>
              <SectionLabel>Onboarding Assigned To</SectionLabel>
              <Value>
                {/* Follows Client Onboarding (Nic 2026-10-09): Reassign there. */}
                <span>
                  {data.claim.onboardingAssignedUser
                    ? `${data.claim.onboardingAssignedUser.userFirstName} ${data.claim.onboardingAssignedUser.userLastName}`
                    : 'Unassigned'}
                </span>
              </Value>
            </Col>
            <Col xs={6}>
              <SectionLabel>Onboarding Assigned At</SectionLabel>
              <Value>
                {data.claim.onboardingAssignedAt
                  ? moment.utc(data.claim.onboardingAssignedAt).format('MM/DD/YYYY HH:mm')
                  : '—'}
              </Value>
            </Col>
            {!showCmCard ? assignedProvidersCol : null}
            {data.claim.partnerId ? (
              <Col xs={6}>
                <SectionLabel>Partner Referral</SectionLabel>
                <Value>{data.claim.partnerName}</Value>
              </Col>
            ) : null}
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <SectionLabel style={{ marginBottom: 0 }}>
              Current Rating
            </SectionLabel>
            {canConfirmRating ? (
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => setShowConfirmRating(true)}
              >
                Update Rating
              </Button>
            ) : null}
          </div>
          <div className="d-flex align-items-center" style={{ gap: '0.6rem' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 800,
                lineHeight: 1.1,
                color: '#0f172a',
              }}
            >
              {currentClaimRating == null ? '--' : `${currentClaimRating}%`}
            </div>
            {Number(currentClaimRating) === 100 ? (
              <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>🎉</span>
            ) : null}
          </div>
          {currentClaimRating != null ||
          (isAdminRoleAdmin && data.claim.claimEstimatedRating != null) ? (
            // 0-100 track: solid indigo = current rating, faint indigo = the
            // estimated target (admin-only, same gate as the numbers below).
            <div
              style={{
                position: 'relative',
                height: '6px',
                borderRadius: '999px',
                background: '#eef2f7',
                overflow: 'hidden',
                marginTop: '0.6rem',
              }}
            >
              {isAdminRoleAdmin && data.claim.claimEstimatedRating != null ? (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: `${Math.min(100, Math.max(0, data.claim.claimEstimatedRating))}%`,
                    background: 'rgba(99, 102, 241, 0.25)',
                    borderRadius: '999px',
                  }}
                />
              ) : null}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: `${Math.min(100, Math.max(0, currentClaimRating || 0))}%`,
                  background: 'linear-gradient(90deg, #6366f1 0%, #4338ca 100%)',
                  borderRadius: '999px',
                }}
              />
            </div>
          ) : null}
          {ratingOpsPills.length > 0 ? (
            <div
              className="d-flex flex-wrap align-items-center"
              style={{ gap: '0.4rem', marginTop: '0.6rem' }}
            >
              {ratingOpsPills.map((pill) => (
                <Badge key={pill.key || pill.label} bg={pill.variant || 'secondary'} pill>
                  {pill.label}
                </Badge>
              ))}
            </div>
          ) : null}
          {ratingLastSetOn ? (
            <div className="text-muted small" style={{ marginTop: '0.5rem' }}>
              Last set {moment.utc(ratingLastSetOn).format('MM/DD/YYYY h:mm A')}
            </div>
          ) : null}
          {isAdminRoleAdmin && data.claim.claimEstimatedRating != null ? (
            <>
              <hr
                style={{
                  border: 'none',
                  borderTop: '1px solid #eef2f7',
                  margin: '1rem 0',
                }}
              />
              <Row>
                <Col xs={6}>
                  <SectionLabel>Estimated Rating</SectionLabel>
                  <Value className="mb-0">
                    {data.claim.claimEstimatedRating}%
                  </Value>
                </Col>
                <Col xs={6}>
                  <SectionLabel>Estimated Increase</SectionLabel>
                  <Value
                    className="mb-0"
                    style={{
                      color:
                        data.claim.claimEstimatedRating -
                          (currentClaimRating || 0) >
                        0
                          ? '#16a34a'
                          : undefined,
                    }}
                  >
                    {data.claim.claimEstimatedRating - (currentClaimRating || 0) > 0
                      ? `+${data.claim.claimEstimatedRating - (currentClaimRating || 0)}%`
                      : `${data.claim.claimEstimatedRating - (currentClaimRating || 0)}%`}
                  </Value>
                </Col>
              </Row>
            </>
          ) : null}
        </Card.Body>
      </Card>

      {/* Submission History — shown only if claim has been submitted */}
      {(() => {
        let history = data.claim.claimSubmissionHistory;
        if (typeof history === 'string') {
          try { history = JSON.parse(history); } catch { history = null; }
        }
        if (!Array.isArray(history)) history = history ? [history] : [];
        if (history.length === 0) return null;

        const TYPE_LABELS = { new_increase: 'New/Increase', supplemental: 'Supplemental', hlr: 'HLR', bdd: 'BDD' };
        const TYPE_COLORS = { new_increase: '#3b82f6', supplemental: '#8b5cf6', hlr: '#f59e0b', bdd: '#22c55e' };

        return (
          <Card className="mb-3">
            <Card.Body>
              <Label>Submissions ({history.length})</Label>
              {history
                .slice()
                .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
                .map((evt, idx) => {
                  const types = Array.isArray(evt.types) ? evt.types
                    : typeof evt.types === 'object' && evt.types !== null ? Object.keys(evt.types)
                    : typeof evt.types === 'string' ? [evt.types] : [];

                  return (
                    <div
                      key={idx}
                      style={{
                        padding: '8px 0',
                        borderTop: idx > 0 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                      }}
                    >
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 4 }}>
                        {moment(evt.submittedAt).format('MM/DD/YYYY')}
                      </div>
                      <div className="d-flex flex-wrap gap-1">
                        {types.map((type) => (
                          <span
                            key={type}
                            style={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 4,
                              background: `${TYPE_COLORS[type] || '#64748b'}15`,
                              color: TYPE_COLORS[type] || '#64748b',
                            }}
                          >
                            {TYPE_LABELS[type] || type}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </Card.Body>
          </Card>
        );
      })()}

      <Card className="mb-3">
        <Card.Body>
          <Label>To Do/Tasks</Label>
          {tasks.length === 0 ? <p>All tasks have been completed.</p> : null}
          {tasks
            .filter((t) => t.claimTaskCompletedOn === null)
            .map((task, idx) => {
              return (
                <Form.Group key={idx}>
                  <Form.Check
                    id={task.claimTaskId}
                    label={task.claimTaskLabel}
                    checked={false}
                    disabled={toggleClaimTask.isLoading}
                    onChange={() =>
                      toggleClaimTask.mutate({
                        claimId: data.claim.claimId,
                        taskId: task.claimTaskId,
                      })
                    }
                  />
                </Form.Group>
              );
            })}
          {tasks.length < totalRemainingTasks ? (
            <Button
              size="sm"
              variant="link"
              className="w-100"
              onClick={() => setShowFullTaskList(true)}
            >
              Show All {totalRemainingTasks}
            </Button>
          ) : (
            <>
              {completedTasks.map((task, idx) => {
                return (
                  <Form.Group key={idx}>
                    <Form.Check
                      id={task.claimTaskId}
                      label={`${task.claimTaskLabel} completed on ${day(
                        task.claimTaskCompletedOn,
                      ).format('MM/DD/YYYY')} by ${task.userFirstName} ${
                        task.userLastName
                      }`}
                      checked={true}
                      disabled={toggleClaimTask.isLoading}
                      onChange={() =>
                        toggleClaimTask.mutate({
                          claimId: data.claim.claimId,
                          taskId: task.claimTaskId,
                        })
                      }
                    />
                  </Form.Group>
                );
              })}
            </>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Label>Notes</Label>
          {data.claim.claimNotes ? (
            <NotesStyled>
              <Markdown remarkPlugins={[noIndentedCode]}>
                {data.claim.claimNotes}
              </Markdown>
            </NotesStyled>
          ) : (
            <p>--</p>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col xs={6}>
              <Label>Phone</Label>
              <Value>{data.user.userPhone}</Value>
            </Col>
            <Col xs={6}>
              <Label>Phone Type</Label>
              <Value>{data.user.userPhoneType}</Value>
            </Col>
          </Row>
          <Label>Email</Label>
          <Value>{data.user.userEmail}</Value>
          <Row>
            <Col xs={6}>
              <Label>Date Of Birth</Label>
              <Value>
                {moment.utc(data.user.userDateOfBirth).format('MM/DD/YYYY')}
              </Value>
            </Col>
            <Col xs={6}>
              <Label>SSN</Label>
              <Value>{data.user.userSsn || '-'}</Value>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <Label>Married</Label>
              <Value>{data.user.userMarried ? 'Yes' : 'No'}</Value>
            </Col>
            <Col xs={6}>
              <Label># Deps. Under 18</Label>
              <Value>{data.user.userDependentsUnder18}</Value>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <Label>Military Status</Label>
              <Value>{data.user.userMilitaryStatus}</Value>
            </Col>
            <Col xs={6}>
              <Label>Combat Veteran</Label>
              <Value>{data.user.userCombatVeteran ? 'Yes' : 'No'}</Value>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <Label>Vietnam Veteran</Label>
              <Value>{data.user.userIsVietnamVeteran ? 'Yes' : 'No'}</Value>
            </Col>
            <Col xs={6}>
              <Label>Discharge Type</Label>
              <Value style={!data.user.userDischargeType ? { color: '#9ca3af' } : undefined}>
                {data.user.userDischargeType || '--'}
              </Value>
            </Col>
          </Row>
          <Label>Service Dates</Label>
          <Value>
            {(data.user.userServiceDates || [])
              .map((dt, idx) => {
                return `${dt.start} to ${dt.end}`;
              })
              .join(', ')}
          </Value>
          {isAdminRoleAdmin && data.claim.claimEstimatedRating != null ? (
            <Row>
              <Col xs={6}>
                <Label>Estimated Rating</Label>
                <Value>
                  {data.claim.claimEstimatedRating}%{' '}
                  <span className="badge bg-secondary">Admin only</span>
                </Value>
              </Col>
              <Col xs={6}>
                <Label>Estimated Increase</Label>
                <Value>
                  {data.claim.claimEstimatedRating - data.claim.claimCurrentRating > 0
                    ? `+${data.claim.claimEstimatedRating - data.claim.claimCurrentRating}%`
                    : `${data.claim.claimEstimatedRating - data.claim.claimCurrentRating}%`}
                </Value>
              </Col>
            </Row>
          ) : null}
          <div
            style={{
              height: '1px',
              backgroundColor: '#e5e7eb',
              margin: '0.75rem 1.5rem',
            }}
          />
          {(() => {
            const u = data.user;
            const line1 = u.addressLine1 || null;
            const line2 = u.addressLine2 || null;
            const city = u.addressCity || null;
            const state = u.addressState || null;
            const zip = u.addressZip || null;
            const verified = !!(u.latitude && u.longitude);
            const hasAny = !!(line1 || city || state || zip);
            const cityState = [city, state].filter(Boolean).join(', ');
            const cityStateZip = [cityState, zip].filter(Boolean).join(' ');
            return (
              <Row>
                <Col xs={12} md={8}>
                  <Label className="d-flex align-items-center" style={{ gap: 8 }}>
                    Address
                    {hasAny &&
                      (verified ? (
                        <span
                          className="px-2 py-0 rounded-pill"
                          style={{
                            fontSize: 10,
                            background: 'rgba(25,135,84,0.12)',
                            color: '#198754',
                          }}
                        >
                          ✓ verified
                        </span>
                      ) : (
                        <span
                          className="px-2 py-0 rounded-pill"
                          style={{
                            fontSize: 10,
                            background: 'rgba(245,158,11,0.15)',
                            color: '#b45309',
                          }}
                          title="No lat/lng on file — admin can edit to verify."
                        >
                          ⚠ unverified
                        </span>
                      ))}
                  </Label>
                  {hasAny ? (
                    <div style={{ lineHeight: 1.4 }}>
                      {line1 && <Value style={{ marginBottom: 0 }}>{line1}</Value>}
                      {line2 && (
                        <Value style={{ marginBottom: 0, color: '#6b7280' }}>
                          {line2}
                        </Value>
                      )}
                      {cityStateZip && (
                        <Value
                          style={{
                            color: line1 ? '#6b7280' : undefined,
                            fontWeight: line1 ? 400 : undefined,
                          }}
                        >
                          {cityStateZip}
                        </Value>
                      )}
                    </div>
                  ) : (
                    <Value style={{ color: '#9ca3af' }}>No address on file</Value>
                  )}
                </Col>
                <Col xs={12} md={4}>
                  <Label>Timezone</Label>
                  <Value>
                    {TIMEZONES.find((tz) => tz.value === u.userTimezone)
                      ?.label || u.userTimezone || '--'}
                  </Value>
                </Col>
              </Row>
            );
          })()}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Row>
            <Col xs={6}>
              <Label>Created On</Label>
              <Value>
                {moment
                  .utc(data.claim.claimCreatedOn)
                  .format('MM/DD/YYYY h:mm A')}
              </Value>
            </Col>
            <Col xs={6}>
              <Label>Updated On</Label>
              <Value>
                {moment
                  .utc(data.claim.claimUpdatedOn)
                  .format('MM/DD/YYYY h:mm A')}
              </Value>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <Label>Intent To File Date</Label>
              <Value>
                {data.claim.claimIntentToFileDate
                  ? moment
                      .utc(data.claim.claimIntentToFileDate)
                      .format('MM/DD/YYYY')
                  : '--'}
              </Value>
            </Col>
            <Col xs={6}>&nbsp;</Col>
          </Row>
        </Card.Body>
      </Card>

      {discountCard}

      {isAdminRoleAdmin ? (
        <Card className="mt-3">
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Claim Status History</h5>
              <span className="badge bg-secondary">Admin only</span>
            </div>
            <div className="text-muted small mt-2">Day + from → to</div>

            <div
              className="mt-3"
              style={{ maxHeight: 320, overflowY: 'auto' }}
            >
              {claimStatusHistoryItems.length === 0 ? (
                <div className="text-muted small">No history found.</div>
              ) : (
                claimStatusHistoryItems.map((item) => (
                  <div key={item.id} className="mb-3">
                    <div className="text-muted small">
                      {moment.utc(item.ts).format('MM/DD/YYYY')}
                    </div>
                    <div className="fw-medium">
                      {item.fromStatus} → {item.toStatus}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card.Body>
        </Card>
      ) : null}

      <EditClaimModal
        show={edit}
        toggle={() => setEdit(false)}
        claim={{ ...data.claim, user: data.user }}
        onSuccess={() => queryClient.invalidateQueries('claim-profile')}
        includeNotes={true}
      />

      <UserPickerModal
        show={showUserPicker !== null}
        toggle={() => setShowUserPicker(null)}
        settings={showUserPicker || {}}
        busy={setClaimAssignment.isLoading}
      />
      <RcCallModal
        show={!!callTarget}
        onHide={() => setCallTarget(null)}
        targetNumber={callTarget || ''}
      />
      <SubmitToVaModal
        show={showSubmitToVa}
        toggle={() => setShowSubmitToVa(false)}
        claim={data.claim}
        currentRating={currentClaimRating}
        submitting={updateClaim.isLoading}
        onConfirm={async (submissionEvent) => {
          updateClaim.mutate(
            {
              claimId: data.claim.claimId,
              payload: {
                claimStatus: 'submitted to va',
                submissionEvent,
              },
            },
            {
              onSuccess: () => {
                setShowSubmitToVa(false);
                queryClient.invalidateQueries('claim-profile');
              },
            },
          );
        }}
      />
      <ConfirmRatingModal
        show={showConfirmRating}
        onHide={() => setShowConfirmRating(false)}
        claim={data.claim}
        currentRating={currentClaimRating}
        confirming={confirmClaimRating.isLoading}
        onConfirm={(body) =>
          confirmClaimRating.mutate({
            claimId: data.claim.claimId,
            body,
          })
        }
      />
      <DiscountRequestModal
        show={showDiscountRequest}
        onHide={() => setShowDiscountRequest(false)}
        discountPresets={discountPresets}
        submitting={requestClaimDiscount.isLoading}
        onSubmit={(body) =>
          requestClaimDiscount.mutate({
            claimId: data.claim.claimId,
            body,
          })
        }
      />
    </>
  );
};

export default SideBar;
